/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import ExcelJS from "exceljs";
import PropTypes from "prop-types";

import { simpleFetch } from "../../restWrapper";
import { downloadBulkTemplate, BASE_HEADERS } from "./bulkTemplate";

import "./BulkUpload.css";

const MAX_PREVIEW_ROWS = 5;
const MAX_ROWS = 1000;
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

const FALLBACKS = {
  title: "Bulk Upload",
  betaBadge: "Beta",
  intro: "Upload many offline transactions at once using an Excel template. Expected columns: {{columns}}, plus any custom form fields.",
  downloadTemplate: "Download template (.xlsx)",
  chooseFile: "Choose file (.xlsx / .csv)",
  chooseDifferentFile: "Choose a different file",
  clear: "Clear",
  rowsLabel: "{{n}} row(s)",
  previewHeader: "Previewing first {{n}} of {{total}} row(s).",
  previewRowHeader: "Row",
  sendReceiptLabel: "Send receipt emails after import (only affects rows marked Completed; Pending rows stay pending)",
  importing: "Importing...",
  importBtn: "Import {{n}} payment(s)",
  lastUploadBanner: "Last bulk upload: {{filename}} — {{count}} payment(s) on {{when}}{{by}}. Make sure this is not a duplicate.",
  lastUploadBy: " by {{email}}",
  lastUploadUnknownFile: "(unknown file)",
  errFailedToGenerateTemplate: "Failed to generate template.",
  errFileTooLarge: "File too large ({{size}} MB). Maximum 5 MB.",
  errUnsupportedType: "Unsupported file type. Use .xlsx or .csv.",
  errTooManyRows: "File has {{n}} rows; maximum is {{max}} per upload.",
  errCouldNotRead: "Could not read the file. Is it a valid Excel/CSV?",
  errUploadFailed: "Upload failed.",
  errUploadFailedRetry: "Upload failed. Please try again.",
  resultIdempotent: "This batch was already imported. ",
  resultCreated: "Created {{created}} of {{total}} payments.",
  resultFailed: " {{n}} row(s) failed.",
  downloadErrorReport: "Download error report",
  errorRowPrefix: "Row {{row}}:",
  errorMore: "...and {{n}} more",
};

const interpolate = (str, vars) => {
  if (!str || !vars) return str;
  let out = str;
  Object.keys(vars).forEach((k) => {
    out = out.replace(new RegExp("\\{\\{" + k + "\\}\\}", "g"), vars[k]);
  });
  return out;
};

const generateBatchToken = () => {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return "batch-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
};

const splitCsvLine = (line) => {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else cur += c;
    } else {
      if (c === ",") { out.push(cur); cur = ""; }
      else if (c === '"') inQuotes = true;
      else cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
};

// Exceljs returns a variety of shapes for cell values. Unwrap hyperlink,
// rich text, formula, and error objects into plain scalars so we never
// render "[object Object]".
const extractCellValue = (raw) => {
  if (raw == null) return null;
  if (raw instanceof Date) return raw;
  if (typeof raw !== "object") return raw;
  if (Array.isArray(raw.richText)) {
    return raw.richText.map((r) => (r && r.text) || "").join("");
  }
  if (typeof raw.text === "string") return raw.text;
  if (raw.hyperlink && typeof raw.hyperlink === "string") {
    return raw.hyperlink.replace(/^mailto:/i, "");
  }
  if (raw.result !== undefined) return raw.result;
  return "";
};

const formatPreviewCell = (value) => {
  const v = extractCellValue(value);
  if (v == null) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 16).replace("T", " ");
  return String(v);
};

const parseCsvText = (text) => {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = splitCsvLine(lines[0]);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = splitCsvLine(lines[i]);
    const obj = { _rowNumber: i + 1 };
    headers.forEach((h, j) => { if (h) obj[h] = values[j] == null ? "" : values[j]; });
    rows.push(obj);
  }
  return { headers, rows };
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString();
};

class BulkUpload extends Component {
  constructor(props) {
    super(props);
    this.state = {
      file: null,
      fileName: "",
      preview: [],
      previewHeaders: [],
      parsedRowCount: 0,
      sendReceipt: false,
      submitting: false,
      error: "",
      parseError: "",
      result: null,
      lastUpload: null,
      batchToken: generateBatchToken(),
    };
    this.fileInputRef = React.createRef();
  }

  t = (key, vars) => {
    const block = (this.props.languageObj && this.props.languageObj.BulkUpload) || {};
    return interpolate(block[key] || FALLBACKS[key] || "", vars);
  };

  componentDidMount() {
    this.fetchLastUpload();
  }

  fetchLastUpload = () => {
    simpleFetch("/organizations/payments/bulk-upload/last", "GET")
      .then((res) => {
        if (res && res.status === 200 && res.body && res.body.lastUpload) {
          this.setState({ lastUpload: res.body.lastUpload });
        }
      })
      .catch(() => {});
  };

  handleDownloadTemplate = async () => {
    try {
      const { organizationObj, missions, language } = this.props;
      await downloadBulkTemplate({ organizationObj, missions, language });
    } catch (err) {
      this.setState({ error: this.t("errFailedToGenerateTemplate") });
    }
  };

  handleFileSelect = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (file.size > MAX_SIZE_BYTES) {
      this.setState({
        parseError: this.t("errFileTooLarge", { size: (file.size / 1024 / 1024).toFixed(1) }),
        file: null,
        fileName: "",
        preview: [],
        parsedRowCount: 0,
      });
      return;
    }

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["xlsx", "csv"].includes(ext)) {
      this.setState({
        parseError: this.t("errUnsupportedType"),
        file: null,
        fileName: "",
        preview: [],
        parsedRowCount: 0,
      });
      return;
    }

    try {
      const { headers, rows } = await this.parseForPreview(file, ext);
      if (rows.length > MAX_ROWS) {
        this.setState({
          parseError: this.t("errTooManyRows", { n: rows.length, max: MAX_ROWS }),
          file: null,
          fileName: "",
          preview: [],
          parsedRowCount: 0,
        });
        return;
      }
      this.setState({
        file,
        fileName: file.name,
        preview: rows.slice(0, MAX_PREVIEW_ROWS),
        previewHeaders: headers,
        parsedRowCount: rows.length,
        parseError: "",
        error: "",
        result: null,
      });
    } catch (err) {
      this.setState({
        parseError: this.t("errCouldNotRead"),
        file: null,
        fileName: "",
        preview: [],
        parsedRowCount: 0,
      });
    }
  };

  parseForPreview = async (file, ext) => {
    if (ext === "csv") {
      const text = await file.text();
      return parseCsvText(text);
    }

    const workbook = new ExcelJS.Workbook();
    const buf = await file.arrayBuffer();
    await workbook.xlsx.load(buf);
    const sheet = workbook.worksheets[0];
    const rows = [];
    let headers = [];
    sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      const values = row.values.slice(1);
      if (rowNumber === 1) {
        headers = values.map((v) => {
          const unwrapped = extractCellValue(v);
          return unwrapped == null ? "" : String(unwrapped).trim();
        });
        return;
      }
      const obj = { _rowNumber: rowNumber };
      headers.forEach((h, i) => {
        if (!h) return;
        obj[h] = extractCellValue(values[i]);
      });
      rows.push(obj);
    });
    return { headers, rows };
  };

  handleToggleSendReceipt = (e) => {
    this.setState({ sendReceipt: e.target.checked });
  };

  handleClearFile = () => {
    this.setState({
      file: null,
      fileName: "",
      preview: [],
      parsedRowCount: 0,
      parseError: "",
      error: "",
      result: null,
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  handleSubmit = async () => {
    const { file, sendReceipt, batchToken } = this.state;
    if (!file) return;

    this.setState({ submitting: true, error: "" });

    const formData = new FormData();
    formData.append("file", file);
    formData.append("batchToken", batchToken);
    formData.append("sendReceipt", sendReceipt ? "true" : "false");

    try {
      const res = await simpleFetch(
        "/organizations/payments/bulk-upload",
        "POST",
        formData,
        true
      );
      if (res && res.status === 200 && res.body) {
        this.setState({
          result: res.body,
          submitting: false,
          batchToken: generateBatchToken(),
        });
        this.fetchLastUpload();
      } else {
        const msg = (res && res.body && res.body.message) || this.t("errUploadFailed");
        this.setState({ error: msg, submitting: false });
      }
    } catch (err) {
      this.setState({ error: this.t("errUploadFailedRetry"), submitting: false });
    }
  };

  renderLastUploadBanner() {
    const { lastUpload } = this.state;
    if (!lastUpload) return null;
    return (
      <div className="BulkUpload-banner">
        {this.t("lastUploadBanner", {
          filename: lastUpload.filename || this.t("lastUploadUnknownFile"),
          count: lastUpload.createdCount,
          when: formatDateTime(lastUpload.createdAt),
          by: lastUpload.createdByEmail
            ? this.t("lastUploadBy", { email: lastUpload.createdByEmail })
            : "",
        })}
      </div>
    );
  }

  renderPreview() {
    const { preview, previewHeaders, parsedRowCount } = this.state;
    if (!preview.length) return null;
    const visibleHeaders = previewHeaders.filter(Boolean);
    return (
      <div className="BulkUpload-preview">
        <div className="BulkUpload-previewHeader">
          {this.t("previewHeader", { n: preview.length, total: parsedRowCount })}
        </div>
        <div className="BulkUpload-previewTableWrap">
          <table className="BulkUpload-previewTable">
            <thead>
              <tr>
                <th>{this.t("previewRowHeader")}</th>
                {visibleHeaders.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((row) => (
                <tr key={row._rowNumber}>
                  <td>{row._rowNumber}</td>
                  {visibleHeaders.map((h) => (
                    <td key={h}>
                      {formatPreviewCell(row[h])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderResult() {
    const { result } = this.state;
    if (!result) return null;
    const hasErrors = result.failed > 0;
    return (
      <div
        className={
          "BulkUpload-result " +
          (hasErrors ? "BulkUpload-result--mixed" : "BulkUpload-result--success")
        }
      >
        <div className="BulkUpload-resultHeader">
          {result.idempotentReplay ? this.t("resultIdempotent") : ""}
          {this.t("resultCreated", { created: result.created, total: result.totalRows })}
          {hasErrors ? this.t("resultFailed", { n: result.failed }) : null}
        </div>
        {hasErrors && result.errorReportUrl ? (
          <div className="BulkUpload-resultActions">
            <a href={result.errorReportUrl} target="_blank" rel="noreferrer">
              {this.t("downloadErrorReport")}
            </a>
          </div>
        ) : null}
        {hasErrors && result.failedPreview && result.failedPreview.length > 0 ? (
          <ul className="BulkUpload-errorList">
            {result.failedPreview.map((e) => (
              <li key={e.row}>
                {this.t("errorRowPrefix", { row: e.row })} {e.errors.join("; ")}
              </li>
            ))}
            {result.failed > result.failedPreview.length ? (
              <li>{this.t("errorMore", { n: result.failed - result.failedPreview.length })}</li>
            ) : null}
          </ul>
        ) : null}
      </div>
    );
  }

  render() {
    const {
      fileName,
      preview,
      parsedRowCount,
      sendReceipt,
      submitting,
      error,
      parseError,
      result,
    } = this.state;

    return (
      <div className="BulkUpload">
        <div className="BulkUpload-header">
          <h2>
            {this.t("title")}{" "}
            <span className="BulkUpload-betaBadge">{this.t("betaBadge")}</span>
          </h2>
        </div>

        <p className="BulkUpload-intro">
          {this.t("intro", { columns: BASE_HEADERS.join(", ") })}
        </p>

        {this.renderLastUploadBanner()}

        <div className="BulkUpload-actions">
          <button
            type="button"
            className="BulkUpload-btn BulkUpload-btn--secondary"
            onClick={this.handleDownloadTemplate}
            disabled={submitting}
          >
            {this.t("downloadTemplate")}
          </button>

          <label
            className={
              "BulkUpload-btn BulkUpload-btn--primary " +
              (submitting ? "BulkUpload-btn--disabled" : "")
            }
          >
            {fileName ? this.t("chooseDifferentFile") : this.t("chooseFile")}
            <input
              ref={this.fileInputRef}
              type="file"
              accept=".xlsx,.csv"
              onChange={this.handleFileSelect}
              disabled={submitting}
              style={{ display: "none" }}
            />
          </label>

          {fileName ? (
            <div className="BulkUpload-fileInfo">
              <span>{fileName}</span> ({this.t("rowsLabel", { n: parsedRowCount })})
              <button
                type="button"
                className="BulkUpload-linkBtn"
                onClick={this.handleClearFile}
                disabled={submitting}
              >
                {this.t("clear")}
              </button>
            </div>
          ) : null}
        </div>

        {parseError ? <div className="BulkUpload-error">{parseError}</div> : null}

        {preview.length ? this.renderPreview() : null}

        {preview.length && !result ? (
          <div className="BulkUpload-commit">
            <label className="BulkUpload-checkbox">
              <input
                type="checkbox"
                checked={sendReceipt}
                onChange={this.handleToggleSendReceipt}
                disabled={submitting}
              />
              {this.t("sendReceiptLabel")}
            </label>

            <button
              type="button"
              className="BulkUpload-btn BulkUpload-btn--primary BulkUpload-submit"
              onClick={this.handleSubmit}
              disabled={submitting}
            >
              {submitting
                ? this.t("importing")
                : this.t("importBtn", { n: parsedRowCount })}
            </button>

            {error ? <div className="BulkUpload-error">{error}</div> : null}
          </div>
        ) : null}

        {this.renderResult()}
      </div>
    );
  }
}

BulkUpload.propTypes = {
  organizationObj: PropTypes.object,
  missions: PropTypes.array,
  language: PropTypes.string,
  languageObj: PropTypes.object,
};

export default BulkUpload;
